/**
 * Grandes cartes à plusieurs versants, décrites par leurs arêtes (voir
 * relief.ts). Chacune démarre avec une petite station au village ; le reste
 * du massif est à équiper : faces au soleil, combes à l'ombre, sommet relié
 * par une télécabine, versants arrière rejoints par la crête.
 */
import type { GameMap } from './map';

/**
 * Les Trois Combes (4 km) : un sommet central d'où partent trois arêtes.
 * Entre elles, la grande combe sud au-dessus du village, et derrière la
 * crête deux combes nord-ouest et nord-est, plus hautes et à l'ombre.
 */
export const TROIS_COMBES: GameMap = {
  id: 'trois-combes',
  name: 'Les Trois Combes',
  stationName: 'Combeloup',
  blurb: 'Un sommet central et trois arêtes : la combe sud au-dessus du village, deux combes nord derrière la crête.',
  faces: ['Sud', 'Nord-Ouest', 'Nord-Est'],
  view: { x: 110, z: 1330, yaw: 0.42 },
  features: {
    seed: 3301,
    size: 4096,
    relief: {
      kind: 'massif',
      floor: 40,
      tilt: [0, -0.03],
      ridges: [
        { pts: [[-60, -380, 720], [-650, -130, 640], [-1150, 330, 520], [-1550, 880, 360], [-1850, 1500, 200]], concave: 0.8 },
        { pts: [[-60, -380, 720], [520, -190, 650], [1050, 250, 540], [1450, 800, 380], [1800, 1450, 220]], concave: 0.8 },
        { pts: [[-60, -380, 720], [40, -950, 600], [220, -1520, 520], [300, -2100, 460]], concave: 0.55 },
      ],
      // Lac de cirque dans le creux naturel de la combe nord-ouest (le replat ne fait que l'aplanir).
      flats: [{ x: -1250, z: -1640, radius: 240, h: 112 }],
    },
    village: { x: 120, z: 1600, radius: 170 },
    lake: { x: -1250, z: -1640, radius: 100 },
  },
  treeLine: 400,
  villageZone: 360,
  lifts: [
    { key: 'granges', kind: 'teleski', name: 'Téléski des Granges', bottom: [300, 1500], top: [325, 1240], color: 2 },
    { key: 'combeloup', kind: 'telesiege', name: 'Télésiège de Combeloup', bottom: [40, 1440], top: [-40, 230], color: 0 },
  ],
  pistes: [
    { name: 'Biche', fromLift: 'granges', toLift: 'granges', gen: { side: 1, grade: 0.11, seed: 1 } },
    { name: 'Marmotte', fromLift: 'combeloup', toLift: 'combeloup', gen: { side: 1, grade: 0.13, seed: 2 } },
    { name: 'Aiglon', fromLift: 'combeloup', toLift: 'combeloup', gen: { side: -1, grade: 0.38, seed: 3 } },
  ],
  buildings: [
    { kind: 'chalet_accueil', x: 130, z: 1545, rot: 0 },
    { kind: 'ticket_office', x: 72, z: 1585, rot: 0.35 },
    { kind: 'welcome_area', x: 150, z: 1690, rot: 0 },
    { kind: 'shop_rental', x: 195, z: 1590, rot: -0.3 },
    { kind: 'lamp_post', x: 102, z: 1620, rot: 0 },
    { kind: 'lamp_post', x: 160, z: 1625, rot: 0 },
    { kind: 'lamp_post', x: 102, z: 1660, rot: 0 },
    { kind: 'lamp_post', x: 185, z: 1650, rot: 0 },
    { kind: 'lamp_post', x: 80, z: 1520, rot: 0 },
    { kind: 'lamp_post', x: 185, z: 1520, rot: 0 },
    // Garage à dameuses entre les deux remontées, portes tournées vers les pistes.
    { kind: 'groomer_garage', x: 175, z: 1430, rot: 3.1416 },
    { kind: 'snow_cannon', x: 0, z: 0, rot: 0, piste: 'Marmotte' },
    { kind: 'snow_cannon', x: 0, z: 0, rot: 0, piste: 'Marmotte' },
    { kind: 'snow_cannon', x: 0, z: 0, rot: 0, piste: 'Marmotte' },
  ],
};

/**
 * Vallée des Arolles (4 km) : une longue vallée entre deux arêtes parallèles,
 * fermée au nord par un col. Le versant ouest regarde l'est (soleil du
 * matin), le versant est regarde l'ouest (soleil de l'après-midi), le fond de
 * vallée remonte vers un cirque exposé au sud.
 */
export const VALLEE_AROLLES: GameMap = {
  id: 'vallee-arolles',
  name: 'Vallée des Arolles',
  stationName: 'Les Arolles',
  blurb: 'Une longue vallée entre deux arêtes : soleil du matin d’un côté, de l’après-midi de l’autre, et un cirque au fond.',
  faces: ['Est', 'Ouest', 'Sud'],
  features: {
    seed: 5417,
    size: 4096,
    relief: {
      kind: 'massif',
      floor: 30,
      tilt: [0, -0.035],
      ridges: [
        { pts: [[-1880, 2000, 300], [-1860, 1100, 440], [-1820, 200, 540], [-1850, -800, 610], [-1600, -1600, 680]], grade: 0.28, concave: 0.85 },
        { pts: [[1880, 2000, 340], [1860, 1100, 520], [1820, 150, 640], [1850, -900, 680], [1600, -1600, 680]], grade: 0.33, concave: 1 },
        { pts: [[-1600, -1600, 680], [-700, -1880, 600], [0, -1950, 540], [700, -1880, 610], [1600, -1600, 680]], grade: 0.3, concave: 0.9 },
      ],
      // Replat du lac, au fond de la vallée sous le cirque.
      flats: [{ x: 0, z: -620, radius: 260, h: 60 }],
    },
    village: { x: 0, z: 950, radius: 180 },
    lake: { x: 0, z: -620, radius: 90 },
  },
  treeLine: 420,
  villageZone: 380,
  lifts: [
    { key: 'granges', kind: 'teleski', name: 'Téléski du Pré', bottom: [-240, 880], top: [-560, 780], color: 2 },
    { key: 'combeloup', kind: 'telesiege', name: 'Télésiège des Arolles', bottom: [-170, 1000], top: [-1620, 720], color: 0 },
  ],
  pistes: [
    { name: 'Biche', fromLift: 'granges', toLift: 'granges', gen: { side: 1, grade: 0.11, seed: 1 } },
    { name: 'Marmotte', fromLift: 'combeloup', toLift: 'combeloup', gen: { side: 1, grade: 0.09, seed: 2 } },
    { name: 'Aiglon', fromLift: 'combeloup', toLift: 'combeloup', gen: { side: 1, grade: 0.5, seed: 3 } },
  ],
  buildings: [
    { kind: 'chalet_accueil', x: 10, z: 895, rot: 0 },
    { kind: 'ticket_office', x: -48, z: 935, rot: 0.35 },
    { kind: 'welcome_area', x: 30, z: 1040, rot: 0 },
    { kind: 'shop_rental', x: 75, z: 940, rot: -0.3 },
    { kind: 'lamp_post', x: -18, z: 970, rot: 0 },
    { kind: 'lamp_post', x: 40, z: 975, rot: 0 },
    { kind: 'lamp_post', x: -18, z: 1010, rot: 0 },
    { kind: 'lamp_post', x: 65, z: 1000, rot: 0 },
    { kind: 'lamp_post', x: -40, z: 870, rot: 0 },
    { kind: 'lamp_post', x: 65, z: 870, rot: 0 },
    { kind: 'groomer_garage', x: -120, z: 1080, rot: 1.5708 },
    { kind: 'snow_cannon', x: 0, z: 0, rot: 0, piste: 'Marmotte' },
    { kind: 'snow_cannon', x: 0, z: 0, rot: 0, piste: 'Marmotte' },
    { kind: 'snow_cannon', x: 0, z: 0, rot: 0, piste: 'Marmotte' },
  ],
};
